import { Component } from "react";
import logger from "../lib/logger";
import { Sentry, isProduction } from "../lib/sentry";

/**
 * Top-level error boundary. Catches any uncaught render-time error from a
 * descendant component, logs it, and shows a calm "Something went wrong"
 * fallback panel instead of a blank page or the dev overlay.
 *
 * Wrap the application root once and forget — it does not break hot reload,
 * routing, or other normal React behaviour.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, info);
    this.setState({ info });
    if (isProduction) {
      try {
        Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
      } catch (sentryErr) {
        // Intentionally swallowed: if Sentry itself errors here, surfacing
        // it would replace the user-visible "Something went wrong" screen
        // with a different uncaught render error, which is strictly worse
        // UX. The underlying `error` is already shown to the user via
        // `componentDidCatch`'s console.error above.
        void sentryErr;
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/login";
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAFA] text-gray-900 p-6">
        <div className="max-w-md w-full border border-gray-300 bg-white p-8 rounded-sm shadow-sm" data-testid="error-boundary">
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-bold mb-2">Unexpected error</div>
          <h2 className="font-black text-2xl tracking-tighter mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-600 mb-5">
            The app hit an unexpected error. Your data is safe — reloading the page usually clears it.
          </p>
          {process.env.NODE_ENV !== "production" && (
            <pre className="text-[11px] font-mono bg-gray-100 border border-gray-200 p-3 rounded-sm max-h-48 overflow-auto mb-5">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
          <div className="flex gap-2">
            <button
              onClick={this.handleReload}
              data-testid="error-reload-button"
              className="bg-[#002FA7] text-white px-4 py-2 rounded-sm font-bold hover:bg-[#002277] flex-1"
            >
              RELOAD
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-home-button"
              className="border border-gray-300 text-gray-900 px-4 py-2 rounded-sm font-bold hover:bg-gray-100"
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }
}
